import { viridis } from "../../../../utils/viridisColorScale";
import { getContrastColorFromRgba } from "../../../../utils/contrastColor";
import { breaks, generalStatistics } from "./utilities";
// Takes the data [values] from numeric observation records
// and the aggregationType (e.g., avg, median, most_recent, or total)
// then outputs a legend object with ranges and colors

export function legendNumericData(values = [], aggregationType = "avg") {
  console.log("Generating numeric data legend");

  // if there are no values, return default legend
  if (!values || values.length === 0) {
    let defaultLegend = {
      scale: null, // equal_interval, equal_count, natural_breaks
      min: null,
      max: null,
      wholeNumber: true,
      useFullValues: false,
      maxCharacters: 0,
      ranges: [],
    };
    return defaultLegend;
  }

  let scale = null;
  const stats = generalStatistics(values);
  console.log("General statistics for numeric data:", stats);

  // break points before adjusting to nice numbers
  let breakPointsRaw = [];
  if (aggregationType === "avg") {
    // assume a normal distribution for averages / means / as it is the mean of means
    // color based on equal interval breaks
    breakPointsRaw = breaks.equalInterval(values, 5);
    scale = "equal_interval";
  } else if (aggregationType === "median") {
    // assume a normal distribution for medians / as it is the median of medians
    // color based on equal count breaks
    breakPointsRaw = breaks.equalCount(values, 5);
    scale = "equal_count";
  } else {
    // assume a non-normal distribution for totals/sums & most most_recent values if (aggregationType === "total" || aggregationType === "most_recent") and any other aggregation type
    // Use natural breaks
    breakPointsRaw = breaks.jenksNatural(values, 5);
    scale = "natural_breaks";
  }

  console.log("Raw break points:", breakPointsRaw);
  // adjust break points to nice numbers
  // use the stats.range to determine rounding. Only round the middle break points leaving the min and max as is.
  // ranges greater than 10, round to nearest whole number
  // ranges between 1 and 10, round to nearest tenth
  // ranges less than 1, round to nearest hundredth
  const breakPointsRounded = breakPointsRaw
    .map((bp, index) => {
      if (stats.wholeNumber) {
        // if all values are whole numbers, round all break points to whole numbers
        return Math.round(bp);
      }
      // after this assume they are not whole numbers
      if (index === 0) {
        // adjust min downwards to 2 decimal places
        // for example, 1043.696428571428 becomes 1043.69
        return Math.floor(bp * 100) / 100;
      }
      if (index === breakPointsRaw.length - 1) {
        // adjust max upwards to 2 decimal places
        // for example, 10123.1111 becomes 10123.12
        return Math.ceil(bp * 100) / 100;
      }
      // Round middle break points based on range
      if (stats.range > 20) {
        return Math.round(bp);
      } else if (stats.range > 2 && stats.range <= 20) {
        return Math.round(bp * 10) / 10;
      } else if (stats.range > 0.2 && stats.range <= 2) {
        return Math.round(bp * 100) / 100;
      } else {
        return Math.round(bp * 1000) / 1000;
      }
    })
    .filter((bp, index, self) => self.indexOf(bp) === index); // remove duplicates
  console.log("After rounding adjustment:", breakPointsRounded);

  // generate ranges based on break points
  let ranges = [];
  for (let i = 0; i < breakPointsRounded.length - 1; i++) {
    const min = breakPointsRounded[i];
    const max = breakPointsRounded[i + 1];

    // range text apears in the legend. If the values are the same, just show one value. Otherwise show min - max. Take into account the rounding adjustments above.
    // for eample, if we rounded to the nearest whole number, subtract 0.1 from the max to avoid overlap in ranges
    let rangeText = "";
    let displayMax = max;
    if (i < breakPointsRounded.length - 2) {
      // not the last range, adjust max for display based on rounding
      if (stats.wholeNumber || stats.range > 200) {
        displayMax = max - 1;
      } else if (stats.range > 20) {
        displayMax = Math.round((max - 0.1) * 10) / 10;
      } else if (stats.range > 2 && stats.range <= 20) {
        displayMax = Math.round((max - 0.01) * 100) / 100;
      } else if (stats.range > 0.2 && stats.range <= 2) {
        displayMax = Math.round((max - 0.001) * 1000) / 1000;
      } else {
        displayMax = Math.round((max - 0.0001) * 10000) / 10000;
      }
    }
    rangeText = min === displayMax ? `${min}` : `${min} - ${displayMax}`;

    // Map index to 0-100 color scale, default to 50 if only one range exists
    const colorValue =
      breakPointsRounded.length - 2 > 0
        ? Math.floor((i / (breakPointsRounded.length - 2)) * 100)
        : 50;
    const backgroundColor = viridis(colorValue);

    const textSymbol = () => {
      // convert index to letters
      let index = i;
      let letters = "";
      do {
        letters = String.fromCharCode((index % 26) + 65) + letters;
        index = Math.floor(index / 26) - 1;
      } while (index >= 0);
      // if letters is empty, return null
      if (letters === "") {
        return null;
      }
      return letters;
    };
    // count how many locations fall within this range
    // last range is inclusive of max value
    const locationCount =
      i === breakPointsRounded.length - 2
        ? values.filter((v) => v >= min && v <= max).length
        : values.filter((v) => v >= min && v < max).length;

    ranges.push({
      text: rangeText,
      symbol: { type: null, size: 16, text: textSymbol() },
      min: min,
      max: max,
      color: getContrastColorFromRgba(backgroundColor),
      backgroundColor: backgroundColor,
      locationCount: locationCount,
    });
  }
  console.log(
    "sum of location counts in ranges:",
    ranges.reduce((acc, r) => acc + r.locationCount, 0),
  );
  let legend = {
    scale: scale,
    min: stats.min,
    max: stats.max,
    wholeNumber: stats.wholeNumber,
    useFullValues: false,
    maxCharacters: stats.maxCharacters,
    ranges: ranges,
  };

  return legend;
}
